/**
 * TanStack Query wrappers for team endpoints.
 *
 * - `useTeams()` lists teams the user is a member of or facilitates
 * - `useTeam(id)` fetches a single team detail (roster + history)
 * - `useTeamByCode(code)` pre-flight lookup for the invite landing page
 * - mutations: createTeam, joinTeam, addStory
 *
 * Mutations invalidate the relevant query keys on success and surface
 * Mantine notifications for errors.
 * @module useTeam
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { teamService } from '../services/teamService'

export function useTeams({ enabled = true } = {}) {
  return useQuery({
    queryKey: ['teams'],
    queryFn: () => teamService.listTeams(),
    staleTime: 30 * 1000,
    // Callers that render before auth (e.g. the global nav) must pass
    // enabled: isAuthenticated — otherwise this fires GET /api/teams on the
    // login page, and a stale token's 401 triggers the auth-redirect loop.
    enabled,
  })
}

export function useTeam(teamId) {
  return useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamService.getTeam(teamId),
    enabled: !!teamId,
    staleTime: 30 * 1000,
  })
}

export function useTeamByCode(code) {
  return useQuery({
    queryKey: ['teamByCode', code],
    queryFn: () => teamService.getTeamByCode(code),
    enabled: !!code,
    retry: false,
    staleTime: 0,
  })
}

export function useCreateTeam() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload) => teamService.createTeam(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
    },
    onError: (error) => {
      notifications.show({
        title: 'Could not create team',
        message: error.response?.data?.error || error.message || 'Unexpected error',
        color: 'red',
      })
    },
  })
}

export function useJoinTeam(teamId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (displayName) => teamService.joinTeam(teamId, displayName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      queryClient.invalidateQueries({ queryKey: ['team', teamId] })
    },
    onError: (error) => {
      notifications.show({
        title: 'Could not join team',
        message: error.response?.data?.error || error.message || 'Unexpected error',
        color: 'red',
      })
    },
  })
}

export function useEndTeamStory(teamId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (teamStoryId) => teamService.endTeamStory(teamId, teamStoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] })
      notifications.show({
        title: 'Story ended',
        message: 'Team analysis is available in Past Stories.',
        color: 'cyan',
      })
    },
    onError: (error) => {
      notifications.show({
        title: 'Could not end story',
        message: error.response?.data?.error || error.message || 'Unexpected error',
        color: 'red',
      })
    },
  })
}

export function useAddStoryToTeam(teamId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (storyId) => teamService.addStory(teamId, storyId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] })
      const lacking = data?.members_lacking_access || []
      if (lacking.length > 0) {
        notifications.show({
          title: 'Team advanced — some members lack access',
          message: `${lacking.length} member(s) cannot play the new story until they're enrolled in the Group.`,
          color: 'yellow',
          autoClose: 8000,
        })
      } else {
        notifications.show({
          title: 'Team advanced',
          message: 'New story is now active.',
          color: 'cyan',
        })
      }
    },
    onError: (error) => {
      notifications.show({
        title: 'Could not add story',
        message: error.response?.data?.error || error.message || 'Unexpected error',
        color: 'red',
      })
    },
  })
}
